import React from 'react';
import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { IconButton, Stack, Tab, Tabs, Tooltip } from '@mui/material';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { isModifiedClick, openInNewTab, resolveAppHref } from '@/utils/navigation';

const TabStrip = ({
	tabs,
	value,
	getTabTo,
	onSelect,
	reorderable,
	droppableProvided,
}) => (
	<Tabs
		indicatorColor='primary'
		scrollButtons='auto'
		textColor='primary'
		value={value}
		onChange={reorderable ? undefined : onSelect}
		sx={{ minWidth: 0, flex: 1 }}
		variant='scrollable'
		slotProps={
			droppableProvided
				? {
						list: {
							ref: droppableProvided.innerRef,
							...droppableProvided.droppableProps,
						},
					}
				: undefined
		}
	>
		{tabs.map((tab, index) => {
			if (!reorderable) {
				return (
					<Tab
						key={tab.value || tab.label}
						sx={{ fontWeight: 'bold' }}
						label={tab.label}
						value={tab.value}
						component={RouterLink}
						to={getTabTo?.(tab) ?? ''}
					/>
				);
			}

			return (
				<Draggable
					key={tab.value || tab.label}
					draggableId={tab.value || tab.label}
					index={index}
					isDragDisabled={tab.locked}
					value={tab.value}
				>
					{(draggableProvided, snapshot) => (
						<Tab
							sx={{
								fontWeight: 'bold',
								cursor: tab.locked
									? 'pointer'
									: snapshot.isDragging
										? 'grabbing'
										: 'grab',
								userSelect: 'none',
							}}
							ref={draggableProvided.innerRef}
							{...draggableProvided.draggableProps}
							{...draggableProvided.dragHandleProps}
							role='tab'
							label={tab.label}
							value={tab.value}
							selected={tab.value === value}
							textColor='primary'
							component={RouterLink}
							to={getTabTo?.(tab) ?? ''}
							onClick={(event) => onSelect(event, tab.value)}
						/>
					)}
				</Draggable>
			);
		})}
		{droppableProvided?.placeholder}
	</Tabs>
);

TabStrip.displayName = 'TabStrip';

export const ReorderableTabs = ({
	tabs = [],
	value,
	getTabTo,
	onTabClick,
	reorderable = false,
	onReorder,
	showReset = false,
	droppableId = 'reorderable-tabs',
}) => {
	const location = useLocation();

	const handleSelect = (event, nextValue) => {
		event.preventDefault();
		const tab = tabs.find((item) => item.value === nextValue) ?? { value: nextValue };
		const to = getTabTo?.(tab) ?? '';
		if (isModifiedClick(event)) {
			openInNewTab(resolveAppHref(to, location.pathname));
			return;
		}
		onTabClick?.(event, nextValue);
	};

	const handleDragEnd = ({ source, destination }) => {
		if (!destination || destination.index === source.index) return;

		const next = tabs.slice();
		const [moved] = next.splice(source.index, 1);
		next.splice(destination.index, 0, moved);

		onReorder?.(next.map((tab) => tab.value));
	};

	const tabStrip = reorderable ? (
		<DragDropContext onDragEnd={handleDragEnd}>
			<Droppable droppableId={droppableId} direction='horizontal'>
				{(droppableProvided) => (
					<TabStrip
						tabs={tabs}
						value={value}
						getTabTo={getTabTo}
						onSelect={handleSelect}
						reorderable
						droppableProvided={droppableProvided}
					/>
				)}
			</Droppable>
		</DragDropContext>
	) : (
		<TabStrip tabs={tabs} value={value} getTabTo={getTabTo} onSelect={handleSelect} />
	);

	return (
		<Stack direction='row' alignItems='center' sx={{ minWidth: 0, flex: 1 }}>
			{tabStrip}
			{reorderable && showReset && (
				<Tooltip title='Reset tab order'>
					<IconButton
						size='small'
						color='primary'
						aria-label='Reset tab order'
						onClick={() => onReorder?.([])}
						sx={{ flexShrink: 0, ml: 0.5 }}
					>
						<RestartAltIcon fontSize='small' />
					</IconButton>
				</Tooltip>
			)}
		</Stack>
	);
};

ReorderableTabs.displayName = 'ReorderableTabs';
